import { Injectable, OnDestroy } from '@angular/core';
import { CssUtils } from '@common/flare-data/util/css-utils';
import { getFlareAttr, updateFlareNodeAttrs } from '@common/flare/prosemirror/transform/node';
import { HTMLStructureItem } from '@common/flare/schema/flare-doc-schema-plugin';
import { toBoolean } from '@common/util/bool';
import { dirname, isAbsoluteUrl, relative, resolvePath } from '@common/util/path';
import { TopicProperties } from '@portal-core/file-properties/models/topic-properties.model';
import { isEqual } from 'lodash';
import { Node } from 'prosemirror-model';
import { EditorState, Transaction } from 'prosemirror-state';
import { debounceTime, map, Observable, ReplaySubject, Subject } from 'rxjs';

/** Node names used when parsing HTML structure items. */
enum NodeName {
  Head = 'head',
  Link = 'link',
  Title = 'title',
}

/** ProseMirror node attribute names used by topic documents. */
enum AttributeName {
  Href = 'href',
  Rel = 'rel',
  Style = 'style',
  Searchable = 'MadCap:searchable',
  HtmlStructure = 'MadCap:htmlStructure',
}

/** CSS style property names used in topic documents. */
enum StylePropertyName {
  TemplatePage = 'mc-template-page',
}

/** Container for the parsed head HTML structure. */
interface HeadContainer {
  /** The full HTML structure array from the document root node attributes. */
  structure: HTMLStructureItem[];
  /** The HTML structure item representing the `<head>` element. */
  item: HTMLStructureItem;
  /** The parsed `<head>` DOM element. */
  head: HTMLElement;
  /** The parsed `<title>` DOM element. */
  title: HTMLElement;
  /** All `<link rel="stylesheet">` elements within the head. */
  links: Element[];
}

/** The service for managing the topic properties document. */
@Injectable()
export class TopicPropertiesService implements OnDestroy {

  private doc: Node;
  private state: EditorState;
  private update$: Subject<void> = new Subject<void>();
  private change$: ReplaySubject<TopicProperties | null> = new ReplaySubject<TopicProperties | null>(1);
  private _filePath: string;

  /** Provides an alternate way to process applying transactions. Can be useful in collaborative editing mode. */
  public dispatchTransaction: (tr: Transaction) => boolean;

  /** Observable that emits the latest topic properties whenever they change. */
  public get topicProperties$(): Observable<TopicProperties | null> {
    return this.change$.asObservable();
  }

  /** Gets the absolute path of the file which the topic properties are associated with,
   * used for resolving relative paths in properties. */
  public get filePath(): string {
    return this._filePath;
  }

  constructor() {
    // Subscribe to document state changes and emit topic properties updates
    this.update$.pipe(
      // ignore rapid state changes before processing
      debounceTime(10),
      map(() => this.topicProperties()),
    ).subscribe(this.change$);
  }

  /** Completes all subjects to prevent memory leaks when the service is destroyed. */
  ngOnDestroy(): void {
    this.update$.complete();
    this.change$.complete();
  }

  /** Synchronously initializes the cache and emits initial topic properties.
   * @param state The initial editor state of the topic document, used to read the initial properties.
   * @param filePath The absolute path of the file which the topic properties are associated with, used for resolving relative paths in properties.
  */
  public initialize(state: EditorState, filePath: string): void {
    this.state = state;
    this.doc = this.state?.doc;
    this._filePath = filePath;
    this.change$.next(this.topicProperties());
  }

  /** Resets the service state, clearing the cache and emitting `null` properties. */
  public reset(): void {
    this.state = null;
    this.doc = null;
    this._filePath = null;
    this.change$.next(null);
  }

  /**
  * Changes the document state if changes are made outside the service.
  * @param state The new editor state to update to.
  */
  public update(state: EditorState): void {
    this.state = state;
    this.doc = this.state?.doc;
    this.update$.next();
  }

  /**
   * Applies updated topic properties to the current document via a ProseMirror transaction.
   * @param newProps The new topic properties to apply.
   * @returns `true` if the transaction was applied, `false` if there is no root node.
   */
  public setTopicProperties(newProps: TopicProperties): boolean {
    if (!this.rootNode)
      return false;
    let hcChanged = false;
    const tr = this.state.tr;
    const hc = this.headContainer();
    const oldProps = this.topicProperties(hc);
    if (oldProps.searchable !== newProps.searchable)
      this.setSearchable(tr, newProps.searchable);
    if (oldProps.templatePage !== newProps.templatePage)
      this.setTemplatePage(tr, newProps.templatePage);
    if (oldProps.title !== newProps.title) {
      this.setTitle(hc, newProps.title);
      hcChanged = true;
    }
    if (!isEqual(oldProps.stylesheets, newProps.stylesheets)) {
      this.setStylesheets(hc, newProps.stylesheets);
      hcChanged = true;
    }
    if (hcChanged)
      this.updateHead(tr, hc);
    return this.applyOperation(tr);
  }

  /** Gets the root element node of the document (the `<html>` element). */
  private get rootNode(): Node {
    return this.doc?.firstChild;
  }

  /** Gets the topic properties model from the current document state.
   * @param container Optional pre-parsed head container to avoid redundant parsing when multiple properties are read together.
   * If not provided, the head will be parsed within this function.
   */
  private topicProperties(container?: HeadContainer): TopicProperties {
    container = container ?? this.headContainer();
    return {
      title: this.title(container),
      stylesheets: this.stylesheets(container),
      templatePage: this.templatePage(),
      searchable: this.searchable(),
    }
  }

  /**
   * Gets whether the topic is searchable.
   * @returns `true` if searchable (default when the attribute is absent), `false` if explicitly disabled.
   */
  private searchable(): boolean {
    if (!this.rootNode)
      return false;
    // we use getFlareAttr to read the searchable attribute because it can be stored as a node attribute
    // or as property in `MadCap:custom` attribute value, and getFlareAttr checks both places
    return toBoolean(getFlareAttr(this.rootNode, AttributeName.Searchable), true);
  }

  /**
   * Adds a step to the transaction to update the searchable attribute on the root node.
   * @param tr The transaction to apply the change to.
   * @param value Whether the topic should be searchable.
   */
  private setSearchable(tr: Transaction, value: boolean): void {
    const searchable = value ? undefined : 'false';
    // we use updateFlareNodeAttrs to additionally remove the searchable value from the `MadCap:custom` attribute
    updateFlareNodeAttrs(tr, this.rootNode, 0, { [AttributeName.Searchable]: searchable }, null);
  }

  /**
   * Gets the absolute path to the template page linked in the document styles.
   * @returns The absolute path, or `null` if not set.
   */
  private templatePage(): string {
    // The template page is stored as a CSS property in the style attribute,
    // the value of this attribute is object with CSS property names as keys and CSS values as values
    const style = this.rootNode?.attrs[AttributeName.Style];
    if (!style)
      return null;
    let value = style[StylePropertyName.TemplatePage];
    if (!value)
      return null;
    let path = CssUtils.parseValueAsLink(value);
    if (!path)
      return null;
    return this.tryResolve(path);
  }

  /**
   * Adds a step to the transaction to update the template page style property on the root node.
   * @param tr The transaction to apply the change to.
   * @param value The absolute path to the template page, or falsy to remove the property.
   */
  private setTemplatePage(tr: Transaction, value: string): void {
    // the style attribute is an object and must be immutable according to ProseMirror docs,
    // so we create a shallow copy to modify
    const current = this.rootNode?.attrs[AttributeName.Style];
    let style = current ? { ...current } : undefined;
    if (!value) {
      if (!style)
        return;
      delete style[StylePropertyName.TemplatePage];
      if (Object.keys(style).length === 0)
        style = undefined;
    } else {
      style = style ?? {};
      const path = this.tryRelative(value);
      const url = CssUtils.toCssUrl(path);
      style[StylePropertyName.TemplatePage] = url;
    }
    tr.setNodeAttribute(0, AttributeName.Style, style);
  }

  /** Gets the topic title text from the head title element.
   * @param container The pre-parsed head container with the title element.
   * @returns The title text, or an empty string if not set.
  */
  private title(container: HeadContainer): string {
    return container.title?.textContent || '';
  }

  /**
   * Adds a step to the transaction to update the title element in the document head with the given title text.
   * @param container The pre-parsed head container with the title element.
   * @param value The new title text, or falsy to remove the title element.
   */
  private setTitle(container: HeadContainer, value: string): void {
    const remove = !value;
    let title = container.title;
    if (remove) {
      if (!title) return;
      title.remove();
    } else {
      this.ensureHead(container);
      // its important to create new links from the same document as the parent element
      // because we parse it with `text/xml` namespace and creating new elements from this document
      // ensures they are in the same namespace which is required for them to be properly serialized in the end
      if (!title) {
        title = container.head.ownerDocument.createElement('title');
        container.head.appendChild(title);
      }
      title.textContent = value;
    }
  }

  /**
   * Gets the list of absolute stylesheet paths linked in the document head.
   * @param container The head container.
   * @returns An array of absolute stylesheet paths.
   */
  private stylesheets(container: HeadContainer): string[] {
    const paths: string[] = [];
    const links = container.links;
    links?.forEach(link => {
      let path = link.getAttribute(AttributeName.Href);
      if (path) paths.push(this.tryResolve(path.trim()));
    });
    return paths;
  }

  /**
   * Adds a step to the transaction to replace the CSS stylesheet links in the document head.
   * @param container The head container with the existing links.
   * @param value The new list of absolute stylesheet paths. Pass `null` or empty to remove all links.
   */
  private setStylesheets(container: HeadContainer, value: string[]): void {
    const remove = !value || value.length === 0;
    const links = container.links;
    if (remove) {
      if (!links?.length) return;
      links.forEach(link => link.remove());
    } else {
      this.ensureHead(container);
      const paths = value.map(value => this.tryRelative(value));
      if (links?.length)
        this.intersectLinks(links, paths);
      // its important to create new links from the same document as the parent element
      // because we parse it with `text/xml` namespace and creating new elements from this document
      // ensures they are in the same namespace which is required for them to be properly serialized in the end
      const doc = container.head.ownerDocument;
      paths.forEach(path => container.head.appendChild(this.createLink(doc, path)));
    }
  }

  /**
   * Parses the `<head>` HTML structure item from the root node and returns parsed elements.
   * @param node The root document node.
   * @returns The container with the head element and its specific elements (title, links).
   */
  private headContainer(): HeadContainer {
    // value of the HTML structure attribute is expected to be an array of objects with nodeName and data properties
    // where the data property is a stringified HTML of the corresponding element
    // shallow copy so callers can mutate the array without altering the immutable ProseMirror node attribute
    const structure = [...(this.rootNode?.attrs[AttributeName.HtmlStructure] as HTMLStructureItem[] ?? [])];
    const index = structure.findIndex(item => item.nodeName.toLowerCase() === NodeName.Head);
    if (index === -1)
      return { structure, item: null, head: null, title: null, links: null };

    // shallow copy of the head item so we can mutate it without altering the immutable ProseMirror node attribute
    const item = { ...structure[index] };
    structure[index] = item;

    // we use `text/xml` namespace to parse the head element
    // to ensure that the stylesheet link elements will be stringified correctly as self-closed tags
    // the `text/html` namespace does not work for this because it stringifies them with only open tag
    const doc = new DOMParser().parseFromString(item.data, 'text/xml');
    const head = doc.firstChild as HTMLElement;

    // XML parsing makes tag names and attribute values case-sensitive, so we filter manually
    // to match the title/link elements regardless of the case of the tag name or the rel value
    const children = head ? Array.from(head.querySelectorAll('*')) : null;
    const title = children?.find(el => el.tagName.toLowerCase() === NodeName.Title) as HTMLElement ?? null;
    const links = children?.filter(el =>
      el.tagName.toLowerCase() === NodeName.Link &&
      el.getAttribute(AttributeName.Rel)?.toLowerCase() === 'stylesheet') ?? null;
    return { structure, item, head, title, links };
  }

  /** Ensures that the head container has a head element, creating one if it does not exist.
   * @param container The head container to ensure the head element on.
  */
  private ensureHead(container: HeadContainer): void {
    if (!container.item) {
      container.item = { nodeName: NodeName.Head, data: '<head></head>' };
      container.structure.unshift(container.item);
    }
    if (!container.head) {
      const doc = new DOMParser().parseFromString(container.item.data, 'text/xml');
      container.head = doc.createElement(NodeName.Head);
      container.item.data = container.head.outerHTML;
    }
  }

  /** Updates the head container with the current head element data.
   * @param tr The transaction to apply the change to.
   * @param container The head container with the updated head element.
  */
  private updateHead(tr: Transaction, container: HeadContainer): void {
    const data = container.head.outerHTML;
    container.item.data = data;
    tr.setNodeAttribute(0, AttributeName.HtmlStructure, container.structure);
  }

  /**
   * Removes existing link elements not present in `paths`, and removes matched entries from `paths`.
   * After this call, `paths` contains only paths that need to be newly appended to the head.
   * @param links The existing stylesheet link elements.
   * @param paths The desired stylesheet paths. Modified in place — matched paths are removed.
   */
  private intersectLinks(links: Element[], paths: string[]): void {
    for (let i = links.length - 1; i >= 0; i--) {
      const url = links[i].getAttribute(AttributeName.Href)?.trim();
      const index = paths.findIndex(path => path === url);
      if (index === -1)
        links[i].remove();
      else
        paths.splice(index, 1);
    }
  }

  /**
   * Creates a new `<link rel="stylesheet">` element.
   * @param doc The owner document used to create the element.
   * @param path The stylesheet href path.
   * @returns The new link element.
   */
  private createLink(doc: Document, path: string): HTMLElement {
    const link = doc.createElement('link');
    link.setAttribute('href', path);
    link.setAttribute('rel', 'stylesheet');
    return link;
  }

  /**
   * Tries to resolve a relative path to a project-wide absolute path based on the current file location.
   * @param path The path to convert.
   * @returns The project-wide absolute path, or the original value if it is already an absolute URL.
   */
  private tryResolve(path: string): string {
    if (!this.filePath || isAbsoluteUrl(path))
      return path;
    return resolvePath(this.filePath, path);
  }

  /**
   * Tries to convert a project-wide absolute path to a path relative to the current file location.
   * @param path The path to convert.
   * @returns The relative path, or the original value if it is an absolute URL.
   */
  private tryRelative(path: string): string {
    if (!this.filePath || isAbsoluteUrl(path))
      return path;
    return relative(dirname(this.filePath), path);
  }

  /**
   * Applies the transaction to the current document.
   * @param tr The transaction that contains operation steps.
   * @returns Returns `true` if the transaction was applied, otherwise returns `false`.
   */
  private applyOperation(tr: Transaction): boolean {
    let applied: boolean = false;
    // exclude this transaction from the editor history
    // since we work outside editor and only with the root node
    tr.setMeta('addToHistory', false);
    if (this.dispatchTransaction) {
      applied = this.dispatchTransaction(tr);
    } else {
      const result = this.state.applyTransaction(tr);
      applied = result.transactions.length > 0;
      this.update(result.state);
    }
    return applied;
  }

}
